import { useState, useEffect } from 'react';

interface UseAgentTransitionLockProps {
  activeTaskId: string | null;
  currentStatus: string | null;
  currentExecutionStatus: string | null;
  currentClarificationContextId: string | null;
  clearLocalClarificationState: (clearSuppression: boolean) => void;
}

export function useAgentTransitionLock({
  activeTaskId,
  currentStatus,
  currentExecutionStatus,
  currentClarificationContextId,
  clearLocalClarificationState,
}: UseAgentTransitionLockProps) {
  const [statusAtSubmission, setStatusAtSubmission] = useState<string | null>(null);
  const [executionStatusAtSubmission, setExecutionStatusAtSubmission] = useState<string | null>(null);
  const [clarificationContextIdAtSubmission, setClarificationContextIdAtSubmission] = useState<string | null>(null);

  // Clean task-switching isolation: resets memory when activeTaskId changes
  useEffect(() => {
    setStatusAtSubmission(null);
    setExecutionStatusAtSubmission(null);
    setClarificationContextIdAtSubmission(null);
  }, [activeTaskId]);

  // Watcher to release lock on status change
  useEffect(() => {
    if (statusAtSubmission === null) return;

    const statusChanged = currentStatus !== statusAtSubmission;
    const executionStatusChanged =
      executionStatusAtSubmission !== null &&
      currentExecutionStatus !== executionStatusAtSubmission;
    const contextChanged =
      clarificationContextIdAtSubmission !== null &&
      currentClarificationContextId !== clarificationContextIdAtSubmission;

    if (statusChanged || executionStatusChanged || contextChanged) {
      clearLocalClarificationState(true);
      setStatusAtSubmission(null);
      setExecutionStatusAtSubmission(null);
      setClarificationContextIdAtSubmission(null);
    }
  }, [
    currentStatus,
    currentExecutionStatus,
    currentClarificationContextId,
    statusAtSubmission,
    executionStatusAtSubmission,
    clarificationContextIdAtSubmission,
    clearLocalClarificationState,
  ]);

  const resetTransitionLock = () => {
    setStatusAtSubmission(null);
    setExecutionStatusAtSubmission(null);
    setClarificationContextIdAtSubmission(null);
  };

  const isStatusUnchangedSinceSubmission =
    statusAtSubmission !== null &&
    currentStatus === statusAtSubmission &&
    (executionStatusAtSubmission === null || currentExecutionStatus === executionStatusAtSubmission) &&
    (clarificationContextIdAtSubmission === null || currentClarificationContextId === clarificationContextIdAtSubmission);

  return {
    statusAtSubmission,
    executionStatusAtSubmission,
    clarificationContextIdAtSubmission,
    setStatusAtSubmission,
    setExecutionStatusAtSubmission,
    setClarificationContextIdAtSubmission,
    resetTransitionLock,
    isStatusUnchangedSinceSubmission,
  };
}
